import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Filter,
  FileText,
  Shield,
  Users,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Car,
  Bus,
  GraduationCap,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Settings,
  Zap,
  Trash2,
  Plane,
  Heart,
  Home,
  Ship,
  X,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Info,
  LayoutGrid,
  List,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { formatFixedDurationLabel } from "../../components/PolicyFormComponents";
import { formatMoney } from "../../utils/formatMoney";

export default function Policies() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [policiesPerPage] = useState(12);

  // Insurance type and policy type filters
  const [selectedInsuranceType, setSelectedInsuranceType] = useState("all");
  const [selectedPolicyType, setSelectedPolicyType] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("policies_view_mode");
    return saved === "list" ? "list" : "grid";
  });
  const [showTips, setShowTips] = useState(() => {
    // Check if user has seen tips before
    const hasSeenTips = localStorage.getItem("policies_tips_seen");
    return !hasSeenTips;
  });

  // Empty policies array - will be populated later
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [isDeletingPolicy, setIsDeletingPolicy] = useState(false);

  // Search + filter derivations
  const searchedPolicies = useMemo(() => {
    const all = policies || [];
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return all;
    return all.filter((p) => {
      const composite = [
        p?.policy_name,
        p?.short_description,
        p?.description,
        p?.policy_type,
        p?.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return composite.includes(query);
    });
  }, [policies, searchQuery]);

  const filteredPolicies = useMemo(() => {
    let filtered = searchedPolicies;

    // Filter by insurance type (only if not "all")
    if (selectedInsuranceType !== "all") {
      filtered = filtered.filter((p) => {
        const insuranceType = p?.short_description || "";
        return insuranceType === selectedInsuranceType;
      });
    }

    // Filter by policy type (for travel and general insurance)
    if (selectedPolicyType) {
      filtered = filtered.filter((p) => {
        return p?.policy_type === selectedPolicyType;
      });
    }

    // Filter by status
    switch (selectedFilter) {
      case "active":
        filtered = filtered.filter((p) => p?.isActive);
        break;
      case "pending":
      case "approved":
      case "rejected":
        filtered = filtered.filter((p) => p?.status === selectedFilter);
        break;
      default:
        break;
    }

    return filtered;
  }, [searchedPolicies, selectedFilter, selectedInsuranceType, selectedPolicyType]);

  useEffect(() => {
    const fetchPolicies = async () => {
      setLoading(true);
      try {
        // If "all" is selected, pass null to fetch all policies
        const shortDescription = selectedInsuranceType !== "all" 
          ? selectedInsuranceType 
          : null;
        
        // Pass policy_type only if it's selected
        const policyType = selectedPolicyType || null;

        const response = await policyService.getAllPolicies(shortDescription, policyType);
        if (response?.data?.data?.status_code === 200) {
          setPolicies([...(response?.data?.data?.data || [])].reverse());
        } else {
          toast.error(
            response?.data?.data?.reason ||
              "An error occurred while fetching policies"
          );
          setPolicies([]);
        }
      } catch (error) {
        toast.error(
          error?.message || "An error occurred while fetching policies"
        );
        setPolicies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
  }, [selectedInsuranceType, selectedPolicyType]);

  // Reset pagination when query or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedFilter, selectedInsuranceType, selectedPolicyType]);

  // Reset policy type filter when insurance type changes
  useEffect(() => {
    setSelectedPolicyType(null);
  }, [selectedInsuranceType]);

  const handleCreatePolicy = () => {
    navigate("/policies/create");
  };

  // Handle delete button click - show confirmation modal
  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!policyToDelete) return;

    setIsDeletingPolicy(true);
    
    try {
      const result = await policyService.deletePolicy(policyToDelete.slug);
      
      if (result.status) {
        toast.success(result.message || "Policy deleted successfully");
        setPolicies(policies.filter((policy) => policy.slug !== policyToDelete.slug));
        setShowDeleteModal(false);
        setPolicyToDelete(null);
      } else {
        toast.error(result.message || "Failed to delete policy");
      }
    } catch (error) {
      toast.error("An unexpected error occurred while deleting the policy");
      console.error("Delete policy error:", error);
    } finally {
      setIsDeletingPolicy(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPolicyToDelete(null);
  };

  // Pagination logic
  const indexOfLastPolicy = currentPage * policiesPerPage;
  const indexOfFirstPolicy = indexOfLastPolicy - policiesPerPage;
  const totalPages = Math.ceil(
    (filteredPolicies?.length || 0) / policiesPerPage
  );
  const currentPolicies =
    filteredPolicies?.slice(indexOfFirstPolicy, indexOfLastPolicy) || [];

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "text-emerald-600";
      case "pending":
        return "text-amber-600";
      case "rejected":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("policies_view_mode", mode);
  };

  const parseCoverageDurations = (policy) => {
    if (!policy?.coverage_durations) return [];
    try {
      if (Array.isArray(policy.coverage_durations)) return policy.coverage_durations;
      if (typeof policy.coverage_durations === "string") {
        const parsed = JSON.parse(policy.coverage_durations);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch {
      return [];
    }
  };

  const getDurationPriceValue = (duration) => {
    if (duration && typeof duration === "object") {
      if (duration.price !== undefined) return duration.price;
      if (duration.premium !== undefined) return duration.premium;
    }
    return 0;
  };

  const getPolicyPriceRange = (policy) => {
    const coverageDurations = parseCoverageDurations(policy);
    if (coverageDurations.length === 0) return { min: 0, max: 0, allQuoteOnly: false };
    const quoteOnlyDurations = coverageDurations.filter((d) => d && d.is_quote_only);
    const pricedDurations = coverageDurations.filter((d) => d && !d.is_quote_only);
    const prices = pricedDurations.map((d) => getDurationPriceValue(d)).filter((p) => p > 0);
    if (prices.length === 0) {
      return {
        min: 0,
        max: 0,
        allQuoteOnly:
          quoteOnlyDurations.length > 0 &&
          quoteOnlyDurations.length === coverageDurations.length,
      };
    }
    return { min: Math.min(...prices), max: Math.max(...prices), allQuoteOnly: false };
  };

  const formatPolicyPrice = (range) => {
    if (range.allQuoteOnly) return "On quote";
    if (range.min === range.max) return formatMoney(range.min);
    return `${formatMoney(range.min)} - ${formatMoney(range.max)}`;
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "approved":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "rejected":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-16 text-center"
    >
      <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-gray-100 rounded-full">
        <FileText className="w-12 h-12 text-gray-400" />
      </div>
      <h3 className="mb-2 text-xl font-semibold text-gray-900">
        No policies found
      </h3>
      <p className="mx-auto mb-8 max-w-md text-gray-500">
        You haven't created any policies yet. Start by creating your first
        policy to manage your insurance offerings.
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCreatePolicy}
        className="inline-flex items-center px-6 py-3 font-medium text-white bg-emerald-600 rounded-lg transition-colors duration-200 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
      >
        <Plus className="mr-2 w-5 h-5" />
        Create New Policy
      </motion.button>
    </motion.div>
  );

  const NoResults = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-16 text-center"
    >
      <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-emerald-50 rounded-full">
        <Search className="w-12 h-12 text-emerald-500" />
      </div>
      <h3 className="mb-2 text-xl font-semibold text-gray-900">
        No matching policies
      </h3>
      <p className="mx-auto mb-8 max-w-md text-gray-500">
        Try adjusting your search or filters. You can also reset to view all
        policies.
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setSearchQuery("");
          setSelectedFilter("all");
        }}
        className="inline-flex items-center px-6 py-3 font-medium text-emerald-700 bg-emerald-50 rounded-lg transition-colors duration-200 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
      >
        Reset Filters
      </motion.button>
    </motion.div>
  );

  const UNIFIED_CARD_COLORS = {
    gradient: "from-emerald-500 to-green-600",
    bgGradient: "from-emerald-50 to-green-50",
    iconColor: "text-emerald-600",
    accentColor: "bg-emerald-500",
  };

  // Helper function to get policy type icon and colors based on insurance type
  const getPolicyTypeInfo = (policyType, shortDescription) => {
    // Determine insurance type from short_description
    const insuranceType = shortDescription || "";
    
    // TripSecure Insurance - uses same policy types as travel but different short_description
    if (insuranceType === "tripsecure_insurance") {
      const tripsecureTypeMap = {
        intercity_travel: {
          icon: Car,
          ...UNIFIED_CARD_COLORS,
          label: "TripSecure Intercity Travel",
          emoji: "🚗",
        },
        okada_rider: {
          icon: Zap,
          ...UNIFIED_CARD_COLORS,
          label: "TripSecure Motor Rider",
          emoji: "🏍️",
        },
      };
      
      if (tripsecureTypeMap[policyType]) {
        return tripsecureTypeMap[policyType];
      }
      
      // Default for tripsecure insurance
      return {
        icon: Car,
        ...UNIFIED_CARD_COLORS,
        label: "TripSecure Insurance",
        emoji: "🚗",
      };
    }
    
    // Life Insurance
    if (insuranceType === "life_insurance" || policyType === "life_insurance") {
      return {
        icon: Shield,
        ...UNIFIED_CARD_COLORS,
        label: "Life Insurance",
        emoji: "🛡️",
      };
    }
    
    // General Insurance - check subcategory
    if (insuranceType === "general_insurance") {
      const subcategoryMap = {
        health_insurance: {
          icon: Users,
          ...UNIFIED_CARD_COLORS,
          label: "Health Insurance",
          emoji: "🏥",
        },
        home_insurance: {
          icon: FileText,
          ...UNIFIED_CARD_COLORS,
          label: "Home Insurance",
          emoji: "🏠",
        },
        accident_insurance: {
          icon: AlertCircle,
          ...UNIFIED_CARD_COLORS,
          label: "Accident Insurance",
          emoji: "⚠️",
        },
        fire_insurance: {
          icon: Zap,
          ...UNIFIED_CARD_COLORS,
          label: "Fire Insurance",
          emoji: "🔥",
        },
        marine_insurance: {
          icon: FileText,
          ...UNIFIED_CARD_COLORS,
          label: "Marine Insurance",
          emoji: "🚢",
        },
        liability_insurance: {
          icon: Shield,
          ...UNIFIED_CARD_COLORS,
          label: "Liability Insurance",
          emoji: "📋",
        },
      };
      
      if (subcategoryMap[policyType]) {
        return subcategoryMap[policyType];
      }
      
      // Default for general insurance
      return {
        icon: FileText,
        ...UNIFIED_CARD_COLORS,
        label: "General Insurance",
        emoji: "📄",
      };
    }
    
    // Travel Insurance types
    const travelTypeMap = {
      daily_commuter: {
        icon: Bus,
        ...UNIFIED_CARD_COLORS,
        label: "Daily Commuter",
        emoji: "🚌",
      },
      intercity_travel: {
        icon: Car,
        ...UNIFIED_CARD_COLORS,
        label: "Intercity Travel",
        emoji: "🚗",
      },
      okada_rider: {
        icon: Zap,
        ...UNIFIED_CARD_COLORS,
        label: "Motor Rider",
        emoji: "🏍️",
      },
      // Legacy types for backward compatibility
      students: {
        icon: GraduationCap,
        ...UNIFIED_CARD_COLORS,
        label: "Students",
        emoji: "🎓",
      },
      driver: {
        icon: Shield,
        ...UNIFIED_CARD_COLORS,
        label: "Driver",
        emoji: "🚗",
      },
      okada: {
        icon: Zap,
        ...UNIFIED_CARD_COLORS,
        label: "Okada",
        emoji: "🏍️",
      },
      taxi: {
        icon: Car,
        ...UNIFIED_CARD_COLORS,
        label: "Taxi",
        emoji: "🚕",
      },
    };
    
    return (
      travelTypeMap[policyType] || {
        icon: FileText,
        ...UNIFIED_CARD_COLORS,
        label:
          policyType
            ?.replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()) || "General",
        emoji: "📄",
      }
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return CheckCircle;
      case "pending":
        return AlertCircle;
      case "rejected":
        return XCircle;
      default:
        return Clock;
    }
  };

  const PolicyItem = ({ policy, index, onDeleteClick }) => {
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    
    // Determine insurance type from short_description
    const insuranceType = policy.short_description || "";
    const isTripSecureInsurance = insuranceType === "tripsecure_insurance";
    const isTravelInsurance = insuranceType === "travel_insurance";
    const isLifeInsurance = insuranceType === "life_insurance";
    const isGeneralInsurance = insuranceType === "general_insurance";
    
    const typeInfo = getPolicyTypeInfo(policy.policy_type, policy.short_description);
    const StatusIcon = getStatusIcon(policy.status);
    const TypeIcon = typeInfo.icon;
    const isIntercity = (isTripSecureInsurance || isTravelInsurance) && policy.policy_type === "intercity_travel";
    const isMotoRider = (isTripSecureInsurance || isTravelInsurance) && policy.policy_type === "okada_rider";
    const isDailyCommuter = (isTripSecureInsurance || isTravelInsurance) && policy.policy_type === "daily_commuter";
    const isFixedCommuter = isMotoRider || isDailyCommuter;

    // Parse coverage durations if they exist
    const coverageDurations = useMemo(() => {
      if (!policy.coverage_durations) return [];

      try {
        // If it's already an array, use it directly
        if (Array.isArray(policy.coverage_durations)) {
          return policy.coverage_durations;
        }

        // If it's a string, try to parse it
        if (typeof policy.coverage_durations === "string") {
          const parsed = JSON.parse(policy.coverage_durations);
          return Array.isArray(parsed) ? parsed : [];
        }

        return [];
      } catch (error) {
        return [];
      }
    }, [policy.coverage_durations]);

    // Separate packages for Life, General, and Travel Insurance
    const packages = useMemo(() => {
      if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
        return coverageDurations.map((pkg, idx) => ({
          ...pkg,
          id: idx,
          // Parse duration string (e.g., "2 years" or "1 years" or "24 hours" or "1 day", etc.)
          durationValue: pkg.duration ? pkg.duration.match(/^(\d+)/)?.[1] || "" : "",
          durationUnit: pkg.duration 
            ? (() => {
                const durationStr = pkg.duration.toString().toLowerCase();
                // Check for new standardized units first
                if (durationStr.includes("hour")) {
                  return durationStr.includes("hours") ? "hours" : "hour";
                } else if (durationStr.includes("day")) {
                  return durationStr.includes("days") ? "days" : "day";
                } else if (durationStr.includes("week")) {
                  // Handle legacy "weekly" format
                  return durationStr.includes("weekly") ? "weeks" : (durationStr.includes("weeks") ? "weeks" : "week");
                } else if (durationStr.includes("month")) {
                  // Handle legacy "monthly" format
                  return durationStr.includes("monthly") ? "months" : (durationStr.includes("months") ? "months" : "month");
                } else if (durationStr.includes("year")) {
                  // Handle legacy "yearly" format
                  return durationStr.includes("yearly") ? "years" : (durationStr.includes("years") ? "years" : "years");
                }
                return "months"; // Default fallback
              })()
            : "years",
        }));
      }
      return [];
    }, [coverageDurations, isLifeInsurance, isGeneralInsurance, isTravelInsurance]);

    // Group routes by type for Intercity
    const { regionalRoutes, internationalRoutes } = useMemo(() => {
      const regional = coverageDurations.filter(d => d.route_type === "regional");
      const international = coverageDurations.filter(d => d.route_type === "international");
      return { regionalRoutes: regional, internationalRoutes: international };
    }, [coverageDurations]);

    // Set default selected route
    useEffect(() => {
      if (isIntercity && coverageDurations.length > 0 && !selectedRoute) {
        setSelectedRoute(coverageDurations[0]);
      }
    }, [coverageDurations, selectedRoute, isIntercity]);

    const getDurationPrice = (duration) => {
      if (duration && typeof duration === "object") {
        // For travel insurance routes
        if (duration.price !== undefined) {
          return duration.price;
        }
        // For life/general insurance packages
        if (duration.premium !== undefined) {
          return duration.premium;
        }
      }
      return 0;
    };

    const getPriceRange = () => {
      if (coverageDurations.length === 0) return { min: 0, max: 0, allQuoteOnly: false };
      const quoteOnlyDurations = coverageDurations.filter(d => d && d.is_quote_only);
      const hasQuoteOnlyPackages = quoteOnlyDurations.length > 0;
      const pricedDurations = coverageDurations.filter(d => d && !d.is_quote_only);
      const prices = pricedDurations.map(d => getDurationPrice(d)).filter(p => p > 0);
      if (prices.length === 0) {
        return { min: 0, max: 0, allQuoteOnly: hasQuoteOnlyPackages && quoteOnlyDurations.length === coverageDurations.length };
      }
      return { min: Math.min(...prices), max: Math.max(...prices), allQuoteOnly: false };
    };

    const priceRange = getPriceRange();

    // Set default selected package for Life/General/Travel Insurance
    useEffect(() => {
      if ((isLifeInsurance || isGeneralInsurance || isTravelInsurance) && packages.length > 0 && !selectedPackage) {
        setSelectedPackage(packages[0]);
      }
    }, [packages, selectedPackage, isLifeInsurance, isGeneralInsurance, isTravelInsurance]);


    return (
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          delay: index * 0.1,
          duration: 0.6,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        whileHover={{
          y: -3,
          transition: { duration: 0.2, ease: "easeOut" },
        }}
        className="overflow-hidden relative group"
      >
        {/* Main Policy Card */}
        <div className="flex overflow-hidden relative flex-col h-full bg-white rounded-xl border border-gray-100 shadow-md transition-all duration-300 ease-out hover:shadow-xl hover:shadow-emerald-500/10">
          {/* Animated Background Gradient */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${typeInfo.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-5`}
          />

          {/* Floating Elements */}
          <div className="absolute top-4 right-4 opacity-0 transition-all duration-500 delay-100 group-hover:opacity-100">
            <div
              className={`w-8 h-8 rounded-full bg-gradient-to-br ${typeInfo.gradient} opacity-20 blur-xl`}
            />
          </div>

          {/* Status Indicator - Top Right */}
          <div className="absolute top-2 right-2 z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.3, duration: 0.3 }}
              className={`inline-flex items-center px-2 py-0.5 rounded-full border backdrop-blur-sm shadow-lg ${getStatusBadgeStyle(
                policy.status
              )}`}
            >
              <StatusIcon
                className={`w-3 h-3 mr-1 ${getStatusColor(policy.status)}`}
              />
              <span className="text-xs font-semibold capitalize">
                {policy.status || "pending"}
              </span>
            </motion.div>
          </div>

          {/* Content Container - Grows to push footer down */}
          <div className="relative flex-1 p-3 pb-0 sm:p-4">
            {/* Header Section */}
            <div className="mb-3">
              {/* Policy Type Badge */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.2 }}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold mb-2 bg-gradient-to-r ${typeInfo.gradient} text-white shadow-md max-w-[calc(100%-72px)]`}
              >
                <TypeIcon className="flex-shrink-0 mr-1.5 w-3 h-3" />
                <span className="truncate">{typeInfo.label}</span>
              </motion.div>

              {/* Policy Name */}
              <motion.h3
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.4 }}
                className="text-base font-bold text-gray-900 mb-1 leading-tight truncate sm:line-clamp-2"
              >
                {policy.policy_name}
              </motion.h3>

              {/* Description */}
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.5 }}
                className="text-xs leading-relaxed text-gray-600 line-clamp-2"
              >
                {policy.short_description || policy.description}
              </motion.p>
            </div>

            {/* Price Range Banner */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.6 }}
              className={`mb-3 p-2.5 rounded-lg bg-gradient-to-r ${typeInfo.bgGradient} border border-emerald-100`}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="mb-0.5 text-[10px] text-emerald-700/70">{priceRange.allQuoteOnly ? "Pricing" : "Price Range"}</p>
                  <p className="text-base font-bold text-emerald-700">
                    {priceRange.allQuoteOnly
                      ? "On quote"
                      : priceRange.min === priceRange.max
                        ? formatMoney(priceRange.min)
                        : `${formatMoney(priceRange.min)} - ${formatMoney(priceRange.max)}`
                    }
                  </p>
                </div>
                <div className={`p-1.5 rounded-md bg-gradient-to-br ${typeInfo.bgGradient} border border-emerald-200`}>
                  <Banknote className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </motion.div>

            {/* Intercity Routes Display */}
            {isIntercity && (regionalRoutes.length > 0 || internationalRoutes.length > 0) && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.7 }}
                className="mb-3"
              >
                {/* Regional Routes */}
                {regionalRoutes.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center mb-2">
                      <div className="mr-2 w-2 h-2 bg-emerald-500 rounded-full" />
                      <h4 className="text-xs font-semibold text-gray-700">Regional Routes</h4>
                      <span className="ml-auto text-[10px] text-gray-500 bg-emerald-50 px-2 py-0.5 rounded-full">
                        {regionalRoutes.length} route{regionalRoutes.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {regionalRoutes.slice(0, 2).map((route, idx) => (
                        <motion.div
                          key={idx}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => setSelectedRoute(route)}
                          className={`p-2 rounded-lg border transition-all cursor-pointer ${
                            selectedRoute === route
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] text-gray-600 mb-0.5">Route</p>
                              <p className="text-xs font-semibold text-gray-900 truncate">
                                {route.route_destination}
                              </p>
                            </div>
                            <div className="ml-2 text-right">
                              <p className="text-[11px] text-gray-600 mb-0.5">Price</p>
                              <p className="text-xs font-bold text-emerald-600">{formatMoney(route.price)}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    {regionalRoutes.length > 2 && (
                      <p className="text-[10px] text-gray-500 mt-1 text-center">
                        +{regionalRoutes.length - 2} more route{regionalRoutes.length - 2 !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

            {internationalRoutes.length > 0 && (
              <div className="flex items-center px-2 py-1.5 rounded-lg bg-emerald-50/80 border border-emerald-100">
                <div className="mr-2 w-2 h-2 bg-emerald-500 rounded-full" />
                <h4 className="text-xs font-semibold text-gray-700">International Routes</h4>
                <span className="ml-auto text-[10px] font-medium text-emerald-700 bg-white px-2 py-0.5 rounded-full">
                  {internationalRoutes.length} route{internationalRoutes.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
              </motion.div>
            )}

            {/* Motor Rider / Daily Commuter Coverage */}
            {isFixedCommuter && coverageDurations.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.7 }}
                className="mb-3"
              >
                <div className={`p-3 rounded-xl bg-gradient-to-br ${typeInfo.bgGradient} border border-emerald-100`}>
                  <div className="space-y-2">
                    {coverageDurations.map((duration, durationIndex) => (
                      <div
                        key={`${duration.display_value}-${duration.display_unit}-${durationIndex}`}
                        className="flex justify-between items-center"
                      >
                        <div>
                          <p className="mb-0.5 text-[10px] text-emerald-700/70">Duration</p>
                          <p className="text-sm font-bold text-gray-900">
                            {formatFixedDurationLabel(duration)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="mb-0.5 text-[10px] text-emerald-700/70">Price</p>
                          <p className="text-lg font-bold text-emerald-700">
                            {formatMoney(getDurationPrice(duration))}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Travel Insurance Packages */}
            {isTravelInsurance && packages.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.7 }}
                className="mb-3"
              >
                <div className="mb-3">
                  <div className="flex items-center mb-2">
                    <Plane className="mr-2 w-4 h-4 text-emerald-500" />
                    <h4 className="text-xs font-semibold text-gray-700">Travel Insurance Packages</h4>
                    <span className="ml-auto text-[10px] text-gray-500 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {packages.length} package{packages.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <div className={`grid gap-2 ${packages.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {packages.slice(0, 2).map((pkg, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedPackage === pkg
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 bg-white hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-gray-600 mb-0.5">Package</p>
                          <p className="text-xs font-semibold text-gray-900 truncate">
                            {pkg.product_name || `Package ${idx + 1}`}
                            {pkg.is_quote_only && (
                              <span className="ml-1 text-[10px] font-medium text-amber-600">(quote)</span>
                            )}
                          </p>
                        </div>
                        <div className="ml-2 text-right">
                          <p className="text-[11px] text-gray-600 mb-0.5">Premium</p>
                          {pkg.is_quote_only ? (
                            <p className="text-xs font-bold text-amber-600">On quote</p>
                          ) : (
                            <>
                              <p className="text-xs font-bold text-emerald-600">{formatMoney(pkg.premium || 0)}</p>
                              {pkg.premium_duration && (
                                <p className="text-[10px] text-gray-500 capitalize">
                                  per {pkg.premium_duration}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-gray-500">
                        <span>Duration: {pkg.duration || "N/A"}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {packages.length > 2 && (
                  <p className="mt-2 text-center text-[10px] font-semibold text-emerald-700">
                    {`{+${packages.length - 2}} more packages`}
                  </p>
                )}
              </motion.div>
            )}

            {/* Life Insurance Packages */}
            {isLifeInsurance && packages.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.7 }}
                className="mb-3"
              >
                <div className="mb-3">
                  <div className="flex items-center mb-2">
                    <Shield className="mr-2 w-4 h-4 text-emerald-500" />
                    <h4 className="text-xs font-semibold text-gray-700">Life Insurance Packages</h4>
                    <span className="ml-auto text-[10px] text-gray-500 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {packages.length} package{packages.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <div className={`grid gap-2 ${packages.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {packages.slice(0, 2).map((pkg, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedPackage === pkg
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 bg-white hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-gray-600 mb-0.5">Package</p>
                          <p className="text-xs font-semibold text-gray-900 truncate">
                            {pkg.product_name || `Package ${idx + 1}`}
                            {pkg.is_quote_only && (
                              <span className="ml-1 text-[10px] font-medium text-amber-600">(quote)</span>
                            )}
                          </p>
                        </div>
                        <div className="ml-2 text-right">
                          <p className="text-[11px] text-gray-600 mb-0.5">Premium</p>
                          {pkg.is_quote_only ? (
                            <p className="text-xs font-bold text-amber-600">On quote</p>
                          ) : (
                            <>
                              <p className="text-xs font-bold text-emerald-600">{formatMoney(pkg.premium || 0)}</p>
                              {pkg.premium_duration && (
                                <p className="text-[10px] text-gray-500 capitalize">
                                  per {pkg.premium_duration}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-gray-500">
                        <span>Duration: {pkg.duration || "N/A"}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {packages.length > 2 && (
                  <p className="mt-2 text-center text-[10px] font-semibold text-emerald-700">
                    {`{+${packages.length - 2}} more packages`}
                  </p>
                )}
              </motion.div>
            )}

            {/* General Insurance Packages */}
            {isGeneralInsurance && packages.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.7 }}
                className="mb-3"
              >
                <div className="mb-3">
                  <div className="flex items-center mb-2">
                    <FileText className="mr-2 w-4 h-4 text-emerald-500" />
                    <h4 className="text-xs font-semibold text-gray-700">Insurance Packages</h4>
                    <span className="ml-auto text-[10px] text-gray-500 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {packages.length} package{packages.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <div className={`grid gap-2 ${packages.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {packages.slice(0, 2).map((pkg, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedPackage === pkg
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 bg-white hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-gray-600 mb-0.5">Package</p>
                          <p className="text-xs font-semibold text-gray-900 truncate">
                            {pkg.product_name || `Package ${idx + 1}`}
                            {pkg.is_quote_only && (
                              <span className="ml-1 text-[10px] font-medium text-amber-600">(quote)</span>
                            )}
                          </p>
                        </div>
                        <div className="ml-2 text-right">
                          <p className="text-[11px] text-gray-600 mb-0.5">Premium</p>
                          {pkg.is_quote_only ? (
                            <p className="text-xs font-bold text-amber-600">On quote</p>
                          ) : (
                            <>
                              <p className="text-xs font-bold text-emerald-600">{formatMoney(pkg.premium || 0)}</p>
                              {pkg.premium_duration && (
                                <p className="text-[10px] text-gray-500 capitalize">
                                  per {pkg.premium_duration}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-gray-500">
                        <span>Duration: {pkg.duration || "N/A"}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {packages.length > 2 && (
                  <p className="mt-2 text-center text-[10px] font-semibold text-emerald-700">
                    {`{+${packages.length - 2}} more packages`}
                  </p>
                )}
              </motion.div>
            )}
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 + 0.8 }}
            className="flex relative z-10 justify-between items-center px-3 pt-2 pb-2.5 bg-white border-t border-gray-100 sm:px-4"
          >
            {/* Quick Actions */}
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <motion.button
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (policy.slug) {
                    navigate(`/policies/view/${policy.slug}`);
                  }
                }}
                aria-label="View policy details"
                className="relative z-10 p-1.5 sm:p-2 text-gray-500 hover:text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all duration-300 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
              >
                <Eye className="w-4 h-4 transition-transform group-hover:scale-110" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (policy.slug || policy.id) {
                    navigate(`/policies/edit/${policy.slug || policy.id}`);
                  }
                }}
                aria-label="Edit policy"
                className="relative z-10 p-1.5 sm:p-2 text-gray-500 hover:text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all duration-300 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
              >
                <Settings className="w-4 h-4 transition-transform group-hover:scale-110" />
              </motion.button>
              <motion.button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteClick(policy);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Delete policy"
                className="relative z-10 p-1.5 sm:p-2 text-gray-500 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all duration-300 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                <Trash2 className="w-4 h-4 transition-transform group-hover:scale-110" />
              </motion.button>
            </div>

            {/* View Details Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (policy.slug) {
                  navigate(`/policies/view/${policy.slug}`);
                }
              }}
              className={`relative z-10 px-2.5 sm:px-3 py-1.5 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 bg-gradient-to-r ${typeInfo.gradient} text-white shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white`}
            >
              View Details
            </motion.button>
          </motion.div>

          {/* Bottom Accent Line */}
          <div className={`h-0.5 bg-gradient-to-r ${typeInfo.gradient}`} />
        </div>
      </motion.div>
    );
  };

  const PolicyTable = ({ policies: tablePolicies, onDeleteClick }) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead className="bg-emerald-50/80 border-b border-emerald-100">
          <tr>
            <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-600 uppercase sm:px-5">
              Policy
            </th>
            <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-600 uppercase sm:px-5">
              Type
            </th>
            <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-600 uppercase sm:px-5">
              Amount
            </th>
            <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-600 uppercase sm:px-5">
              Status
            </th>
            <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-600 uppercase text-right sm:px-5">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {tablePolicies.map((policy, index) => {
            const typeInfo = getPolicyTypeInfo(policy.policy_type, policy.short_description);
            const TypeIcon = typeInfo.icon;
            const StatusIcon = getStatusIcon(policy.status);
            const priceRange = getPolicyPriceRange(policy);

            return (
              <motion.tr
                key={policy.id || index}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.25 }}
                onClick={() => policy.slug && navigate(`/policies/view/${policy.slug}`)}
                className="transition-colors cursor-pointer hover:bg-emerald-50/60"
              >
                <td className="px-4 py-3.5 sm:px-5">
                  <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                    {policy.policy_name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 line-clamp-1 max-w-xs">
                    {policy.description || "—"}
                  </p>
                </td>
                <td className="px-4 py-3.5 sm:px-5">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gradient-to-r ${typeInfo.gradient} text-white`}>
                    <TypeIcon className="w-3 h-3" />
                    <span className="whitespace-nowrap">{typeInfo.label}</span>
                  </span>
                </td>
                <td className="px-4 py-3.5 sm:px-5">
                  <p className="text-sm font-bold text-emerald-700 whitespace-nowrap">
                    {formatPolicyPrice(priceRange)}
                  </p>
                </td>
                <td className="px-4 py-3.5 sm:px-5">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${getStatusBadgeStyle(
                      policy.status
                    )}`}
                  >
                    <StatusIcon className={`w-3 h-3 mr-1 ${getStatusColor(policy.status)}`} />
                    {policy.status || "pending"}
                  </span>
                </td>
                <td className="px-4 py-3.5 sm:px-5">
                  <div className="flex justify-end items-center gap-1">
                    <button
                      type="button"
                      aria-label="View policy details"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (policy.slug) navigate(`/policies/view/${policy.slug}`);
                      }}
                      className="p-1.5 text-gray-500 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Edit policy"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (policy.slug || policy.id) {
                          navigate(`/policies/edit/${policy.slug || policy.id}`);
                        }
                      }}
                      className="p-1.5 text-gray-500 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete policy"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteClick(policy);
                      }}
                      className="p-1.5 text-gray-500 rounded-lg transition-colors hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const Pagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        {/* Previous Button */}
        <button
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg border transition-all duration-200 ${
            currentPage === 1
              ? "border-gray-200 text-gray-400 cursor-not-allowed"
              : "border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page Numbers */}
        {pageNumbers.map((number, index) => (
          <button
            key={index}
            onClick={() => typeof number === "number" && paginate(number)}
            className={`px-3 py-2 rounded-lg border transition-all duration-200 ${
              number === currentPage
                ? "bg-emerald-600 border-emerald-600 text-white"
                : typeof number === "number"
                ? "border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
                : "border-transparent text-gray-400 cursor-default"
            }`}
          >
            {number}
          </button>
        ))}

        {/* Next Button */}
        <button
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg border transition-all duration-200 ${
            currentPage === totalPages
              ? "border-gray-200 text-gray-400 cursor-not-allowed"
              : "border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
          }`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-emerald-50 to-white via-green-50/30">
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1 min-w-0">
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl"
              >
                Policy Management
              </motion.h1>
              <p className="text-sm text-gray-600 sm:text-base">
                Manage your insurance policies, view details, and create new
                offerings
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setShowTips(!showTips);
                if (showTips) {
                  localStorage.setItem("policies_tips_seen", "true");
                }
              }}
              className="flex gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-emerald-700 whitespace-nowrap bg-emerald-50 rounded-lg border border-emerald-200 transition-colors sm:px-4 sm:text-sm hover:bg-emerald-100"
            >
              <Lightbulb className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{showTips ? "Hide Tips" : "Show Tips"}</span>
              <span className="sm:hidden">{showTips ? "Hide" : "Tips"}</span>
              {showTips ? (
                <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Tips and Guide Section */}
        {showTips && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-4 mb-4 bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 rounded-xl border shadow-lg sm:mb-6 sm:p-5 lg:p-6 sm:rounded-2xl border-emerald-200/50"
          >
            <div className="flex flex-col gap-3 items-start sm:flex-row sm:gap-4">
              <div className="flex-shrink-0 p-2.5 sm:p-3 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-lg sm:rounded-xl text-white">
                <Info className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-2 text-base font-bold text-gray-900 sm:mb-3 sm:text-lg">
                  Welcome to Policy Management! 👋
                </h3>
                <p className="mb-3 text-xs text-gray-700 sm:mb-4 sm:text-sm">
                  Here's a quick guide to help you get started with managing your insurance policies:
                </p>
                
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
                  {/* Tip 1 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-emerald-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                        <Search className="w-4 h-4 text-emerald-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Search & Filter
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          Use the search bar to find policies by name, type, or description. Click "Advanced Filters" to filter by insurance type (Travel, Life, General) and specific policy categories.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tip 2 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-blue-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                        <Filter className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Insurance Types
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          <strong>TripSecure Insurance:</strong> Daily Commuter, Intercity Travel, Motor Rider<br />
                          <strong>Travel Insurance:</strong> Flexible packages with hours, hour, day, days, week, weeks, month, months, years<br />
                          <strong>Life Insurance:</strong> Comprehensive life coverage<br />
                          <strong>General Insurance:</strong> Health, Home, Accident, Fire, Marine, Liability
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tip 3 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-purple-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                        <Plus className="w-4 h-4 text-purple-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Create New Policy
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          Click the "Create Policy" button to start creating a new insurance policy. You'll be guided through selecting the insurance type and filling in all necessary details.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tip 4 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-amber-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                        <Eye className="w-4 h-4 text-amber-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Policy Actions
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          Each policy card has action buttons: <strong>View</strong> (👁️) to see details, <strong>Edit</strong> (⚙️) to modify, and <strong>Delete</strong> (🗑️) to remove. Use "View Details" for a comprehensive overview.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tip 5 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-indigo-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-indigo-100 rounded-lg">
                        <Shield className="w-4 h-4 text-indigo-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Policy Status
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          Policies can have different statuses: <strong>Pending</strong> (awaiting approval), <strong>Approved</strong> (active), or <strong>Rejected</strong>. Filter by status to manage them effectively.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tip 6 */}
                  <div className="p-3 rounded-lg border sm:p-4 bg-white/80 sm:rounded-xl border-rose-200/50">
                    <div className="flex gap-2 items-start sm:gap-3">
                      <div className="flex-shrink-0 p-1.5 sm:p-2 bg-rose-100 rounded-lg">
                        <FileText className="w-4 h-4 text-rose-600 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-xs font-semibold text-gray-900 sm:text-sm">
                          Quick Stats
                        </h4>
                        <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                          The stats cards at the top show: Total Policies, Active count, Pending approvals, and Approved policies. These update in real-time as you filter.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-emerald-100/50 rounded-lg border border-emerald-200">
                  <p className="text-[11px] sm:text-xs text-emerald-800">
                    <strong>💡 Pro Tip:</strong> Use the status filter dropdown to quickly find policies that need attention. Combine it with search and insurance type filters for precise results!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="p-3 mb-4 rounded-xl border shadow-lg backdrop-blur-sm sm:p-4 lg:p-6 sm:mb-6 sm:rounded-2xl bg-white/80 border-gray-200/50"
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
            {/* Search and Filters */}
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 sm:flex-1 lg:flex-initial">
              {/* Search Bar */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search policies"
                  className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-300 sm:pr-4 sm:pl-10 sm:text-base sm:w-56 lg:w-64 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

             
            </div>

            {/* Create Policy Button */}
            <div className="flex flex-shrink-0 gap-2 items-center sm:gap-3">
              <div
                className="inline-flex items-center p-1 bg-gray-100 rounded-lg"
                role="group"
                aria-label="Switch between grid and list view"
              >
                <button
                  type="button"
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                  onClick={() => handleViewModeChange("grid")}
                  className={`inline-flex items-center justify-center p-2 rounded-md transition-all ${
                    viewMode === "grid"
                      ? "bg-white text-emerald-700 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                  onClick={() => handleViewModeChange("list")}
                  className={`inline-flex items-center justify-center p-2 rounded-md transition-all ${
                    viewMode === "list"
                      ? "bg-white text-emerald-700 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 whitespace-nowrap ${
                  showFilters || (selectedInsuranceType !== "all") || selectedPolicyType
                    ? "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500"
                }`}
              >
                <Filter className="mr-1.5 sm:mr-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Advanced Filters</span>
                <span className="sm:hidden">Filters</span>
                {((selectedInsuranceType !== "all") || selectedPolicyType) && (
                  <span className="ml-1.5 sm:ml-2 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold bg-white text-emerald-600 rounded-full">
                    {[selectedInsuranceType !== "all" && 1, selectedPolicyType && 1].filter(Boolean).length}
                  </span>
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreatePolicy}
                className="inline-flex justify-center items-center px-3 py-2 text-xs font-medium text-white whitespace-nowrap bg-emerald-600 rounded-lg transition-colors duration-200 sm:px-4 sm:text-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                <Plus className="mr-1.5 sm:mr-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Create Policy</span>
                <span className="sm:hidden">Create</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Advanced Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="p-6 mb-6 rounded-2xl border shadow-lg backdrop-blur-sm bg-white/90 border-gray-200/50"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Filter Policies</h3>
                <p className="mt-1 text-sm text-gray-600">Refine your search by insurance type and policy category</p>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 text-gray-400 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Insurance Type Filters */}
            <div className="mb-6">
              <label className="block mb-3 text-sm font-semibold text-gray-700">
                Insurance Type
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {/* All Insurance Types */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedInsuranceType("all");
                    setSelectedPolicyType(null);
                  }}
                  className={`relative overflow-hidden p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                    selectedInsuranceType === "all"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-500/20"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      selectedInsuranceType === "all"
                        ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedInsuranceType === "all" ? "text-emerald-900" : "text-gray-900"
                      }`}>
                        All Types
                      </p>
                      <p className="text-xs text-gray-500">View all policies</p>
                    </div>
                  </div>
                </motion.button>

                {/* TripSecure Insurance */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedInsuranceType("tripsecure_insurance")}
                  className={`relative overflow-hidden p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                    selectedInsuranceType === "tripsecure_insurance"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-500/20"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      selectedInsuranceType === "tripsecure_insurance"
                        ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                        : "bg-gray-100 text-emerald-600"
                    }`}>
                      <Car className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedInsuranceType === "tripsecure_insurance" ? "text-emerald-900" : "text-gray-900"
                      }`}>
                        TripSecure
                      </p>
                      <p className="text-xs text-gray-500">
                        {policies.filter(p => p?.short_description === "tripsecure_insurance").length} policies
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* Travel Insurance */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedInsuranceType("travel_insurance")}
                  className={`relative overflow-hidden p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                    selectedInsuranceType === "travel_insurance"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-500/20"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      selectedInsuranceType === "travel_insurance"
                        ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                        : "bg-gray-100 text-emerald-600"
                    }`}>
                      <Plane className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedInsuranceType === "travel_insurance" ? "text-emerald-900" : "text-gray-900"
                      }`}>
                        Travel Insurance
                      </p>
                      <p className="text-xs text-gray-500">
                        {policies.filter(p => p?.short_description === "travel_insurance").length} policies
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* Life Insurance */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedInsuranceType("life_insurance")}
                  className={`relative overflow-hidden p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                    selectedInsuranceType === "life_insurance"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-500/20"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      selectedInsuranceType === "life_insurance"
                        ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                        : "bg-gray-100 text-emerald-600"
                    }`}>
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedInsuranceType === "life_insurance" ? "text-emerald-900" : "text-gray-900"
                      }`}>
                        Life Insurance
                      </p>
                      <p className="text-xs text-gray-500">
                        {policies.filter(p => p?.short_description === "life_insurance").length} policies
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* General Insurance */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedInsuranceType("general_insurance")}
                  className={`relative overflow-hidden p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                    selectedInsuranceType === "general_insurance"
                      ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-500/20"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      selectedInsuranceType === "general_insurance"
                        ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                        : "bg-gray-100 text-emerald-600"
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedInsuranceType === "general_insurance" ? "text-emerald-900" : "text-gray-900"
                      }`}>
                        General Insurance
                      </p>
                      <p className="text-xs text-gray-500">
                        {policies.filter(p => p?.short_description === "general_insurance").length} policies
                      </p>
                    </div>
                  </div>
                </motion.button>
              </div>
            </div>

            {/* Policy Type Filters - TripSecure Insurance */}
            {selectedInsuranceType === "tripsecure_insurance" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <label className="block mb-3 text-sm font-semibold text-gray-700">
                  TripSecure Policy Type
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    { value: "daily_commuter", label: "Daily Commuter", icon: Bus, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                    { value: "intercity_travel", label: "Intercity Travel", icon: Car, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                    { value: "okada_rider", label: "Motor Rider", icon: Zap, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                  ].map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedPolicyType === type.value;
                    return (
                      <motion.button
                        key={type.value}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPolicyType(isSelected ? null : type.value)}
                        className={`relative overflow-hidden p-3 text-left rounded-lg border-2 transition-all duration-300 shadow-md ${
                          isSelected
                            ? `${type.selectedClasses} ring-2`
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon className={`w-4 h-4 ${
                            isSelected ? type.iconClasses : "text-gray-500"
                          }`} />
                          <span className={`text-xs font-medium ${
                            isSelected ? type.textClasses : "text-gray-700"
                          }`}>
                            {type.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Policy Type Filters - Travel Insurance */}
            {selectedInsuranceType === "travel_insurance" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <label className="block mb-3 text-sm font-semibold text-gray-700">
                  Travel Policy Type
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    { value: "daily_commuter", label: "Daily Commuter", icon: Bus, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                    { value: "intercity_travel", label: "Intercity Travel", icon: Car, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                    { value: "okada_rider", label: "Motor Rider", icon: Zap, selectedClasses: "border-emerald-500 bg-emerald-50 ring-emerald-500/20", iconClasses: "text-emerald-600", textClasses: "text-emerald-900" },
                  ].map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedPolicyType === type.value;
                    return (
                      <motion.button
                        key={type.value}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPolicyType(isSelected ? null : type.value)}
                        className={`relative overflow-hidden p-3 text-left rounded-lg border-2 transition-all duration-300 shadow-md ${
                          isSelected
                            ? `${type.selectedClasses} ring-2`
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon className={`w-4 h-4 ${
                            isSelected ? type.iconClasses : "text-gray-500"
                          }`} />
                          <span className={`text-xs font-medium ${
                            isSelected ? type.textClasses : "text-gray-700"
                          }`}>
                            {type.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Policy Type Filters - General Insurance */}
            {selectedInsuranceType === "general_insurance" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <label className="block mb-3 text-sm font-semibold text-gray-700">
                  General Insurance Category
                </label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {[
                    { value: "health_insurance", label: "Health", icon: Heart },
                    { value: "home_insurance", label: "Home", icon: Home },
                    { value: "accident_insurance", label: "Accident", icon: AlertCircle },
                    { value: "fire_insurance", label: "Fire", icon: Zap },
                    { value: "marine_insurance", label: "Marine", icon: Ship },
                    { value: "liability_insurance", label: "Liability", icon: Shield },
                  ].map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedPolicyType === type.value;
                    return (
                      <motion.button
                        key={type.value}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPolicyType(isSelected ? null : type.value)}
                        className={`relative overflow-hidden p-3 text-center rounded-lg border-2 transition-all duration-300 ${
                          isSelected
                            ? "bg-emerald-50 border-emerald-500 ring-2 shadow-md ring-emerald-500/20"
                            : "bg-white border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-1">
                          <Icon className={`w-5 h-5 ${
                            isSelected ? "text-emerald-600" : "text-gray-500"
                          }`} />
                          <span className={`text-xs font-medium ${
                            isSelected ? "text-emerald-900" : "text-gray-700"
                          }`}>
                            {type.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Clear Filters Button */}
            {((selectedInsuranceType !== "all") || selectedPolicyType) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-end"
              >
                <button
                  onClick={() => {
                    setSelectedInsuranceType("all");
                    setSelectedPolicyType(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  Clear All Filters
                </button>
              </motion.div>
            )}
          </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Stats (Always show with 0 counts when empty) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-6 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-4 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300 sm:p-5 bg-white/80 border-gray-200/50 hover:shadow-xl"
          >
            <div className="flex items-center">
              <div className="p-2.5 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl shadow-sm">
                <FileText className="w-5 h-5 text-emerald-600 sm:w-6 sm:h-6" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  Total Policies
                </p>
                <p className="text-lg font-bold text-gray-900 sm:text-xl">
                  {policies?.length || 0}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-4 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300 sm:p-5 bg-white/80 border-gray-200/50 hover:shadow-xl"
          >
            <div className="flex items-center">
              <div className="p-2.5 bg-gradient-to-br from-green-100 to-green-200 rounded-xl shadow-sm">
                <Shield className="w-5 h-5 text-green-600 sm:w-6 sm:h-6" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  Active
                </p>
                <p className="text-lg font-bold text-gray-900 sm:text-xl">
                  {policies?.filter((p) => p.isActive)?.length || 0}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-4 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300 sm:p-5 bg-white/80 border-gray-200/50 hover:shadow-xl"
          >
            <div className="flex items-center">
              <div className="p-2.5 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl shadow-sm">
                <Users className="w-5 h-5 text-amber-600 sm:w-6 sm:h-6" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  Pending
                </p>
                <p className="text-lg font-bold text-gray-900 sm:text-xl">
                  {policies?.filter((p) => p.status === "pending")?.length || 0}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-4 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300 sm:p-5 bg-white/80 border-gray-200/50 hover:shadow-xl"
          >
            <div className="flex items-center">
              <div className="p-2.5 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl shadow-sm">
                <Banknote className="w-5 h-5 text-emerald-600 sm:w-6 sm:h-6" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  Approved
                </p>
                <p className="text-lg font-bold text-gray-900 sm:text-xl">
                  {policies?.filter((p) => p.status === "approved")?.length ||
                    0}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Content Section */}
        <div className="rounded-2xl border shadow-lg backdrop-blur-sm bg-white/80 border-gray-200/50">
          {loading ? (
            <div className="flex justify-center items-center py-24 min-h-[400px]">
              <div className="flex flex-col gap-4 items-center">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-600"></div>
                  <div className="absolute inset-0 w-12 h-12 rounded-full border-4 border-transparent animate-pulse border-t-emerald-400"></div>
                </div>
                <p className="text-sm font-medium text-gray-600">Loading policies...</p>
                <p className="text-xs text-gray-500">Please wait while we fetch your policies</p>
              </div>
            </div>
          ) : !policies || policies.length === 0 ? (
            <EmptyState />
          ) : filteredPolicies.length === 0 ? (
            <NoResults />
          ) : (
            <div className={viewMode === "list" ? "" : "p-4 sm:p-5 lg:p-6"}>
              {viewMode === "list" ? (
                <PolicyTable
                  policies={currentPolicies}
                  onDeleteClick={handleDeleteClick}
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
                  {currentPolicies.map((policy, index) => (
                    <PolicyItem
                      key={policy.id || index}
                      policy={policy}
                      index={index}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className={viewMode === "list" ? "px-4 pb-4 sm:px-5 lg:px-6" : ""}>
                  <Pagination />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Policy"
        message={
          policyToDelete 
            ? `Are you sure you want to delete "${policyToDelete.policy_name}"? This action cannot be undone and will permanently remove the policy and all associated data.`
            : "Are you sure you want to delete this policy?"
        }
        confirmText="Delete Policy"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeletingPolicy}
      />
    </div>
  );
}
